import { isDef } from '@utils/def';

import { ActionType, getType } from 'typesafe-actions';

import { HedgingInstrument } from '../../../../models/HedgingInstrument';
import { LegTerms } from '../../../../models/LegTerms';
import { Metric } from '../../../../models/Metric';
import { Valuation } from '../../../../models/Valuation';

import { FiledDownloadItem } from '../../components/ButtonActions/components/UpdateTrade/components/UploadFiles/types';
import { FxRateLockerModes, PricerModes } from '../../enums';

import * as pricerActions from './actions';
import { FlowsState } from './components/FlowsReducer';
import legsReducer, { LegsState } from './components/LegsReducer';
import masterLegReducer, {
  MasterLegState,
} from './components/MasterLegReducer';
import { empty_master_leg_terms } from './components/MasterLegReducer/constants';
import tradeSummaryReducer, {
  TradeSummaryInitialState,
  TradeSummaryState,
} from './components/TradeSummaryReducer';
import valuationReducer, {
  ValuationInitialState,
  ValuationState,
} from './components/ValuationReducer';
import {
  ApprovalRequest,
  ExecutionCriteria,
  FxAveragingOptions,
  FxOptions,
} from './types';

export type PricerActionType = ActionType<typeof pricerActions>;

export type PricerState = {
  hedgingInstrument: undefined | HedgingInstrument['instrument_id'];
  transaction: {
    transaction_id: null | number;
    trade_summary: TradeSummaryState;
    legs: LegsState;
    transaction_metrics: Metric[];
    transaction_valuation: Valuation[];
    valuationDates: ValuationState;
    master_leg: MasterLegState;
    option: FxOptions | null;
    fx_averaging: FxAveragingOptions | null;
    execution_criteria: ExecutionCriteria | null;
    pricerType: PricerModes | undefined;
    approval_request: null | ApprovalRequest;
    percentage: number | null;
  };
  filesForDownload: FiledDownloadItem[];
  buildScheduleLoading: boolean;
  saveTradeLoading: boolean;
  valueTradeLoading: boolean;
  transactionLoading: boolean;
  filesForDownloadLoading: boolean;
  reviewTradeLoading: boolean;
  getTransactionFailed: boolean;
  options: {
    spot: { data: null | number; loading: boolean };
    forward: { data: null | number; loading: boolean };
    fxRate: { data: number; lockerValue: FxRateLockerModes };
  };
};

const initialState: PricerState = {
  hedgingInstrument: undefined,
  transaction: {
    transaction_id: null,
    trade_summary: TradeSummaryInitialState,
    legs: {},
    transaction_metrics: [],
    transaction_valuation: [],
    valuationDates: ValuationInitialState,
    master_leg: empty_master_leg_terms,
    option: null,
    fx_averaging: null,
    execution_criteria: null,
    pricerType: undefined,
    approval_request: null,
    percentage: null,
  },
  filesForDownload: [],
  buildScheduleLoading: false,
  saveTradeLoading: false,
  valueTradeLoading: false,
  transactionLoading: false,
  filesForDownloadLoading: false,
  reviewTradeLoading: false,
  getTransactionFailed: false,
  options: {
    spot: { data: null, loading: false },
    forward: { data: null, loading: false },
    fxRate: { data: 1, lockerValue: FxRateLockerModes.rightLeg },
  },
};

const pricerReducer = (
  state: PricerState = initialState,
  action: PricerActionType,
): PricerState => {
  switch (action.type) {
    case getType(pricerActions.setHedgingInstrument):
      return {
        ...state,
        hedgingInstrument: action.payload.instrument_type_id,
        transaction: {
          ...state.transaction,
          legs: legsReducer(state.transaction.legs, action),
        },
      };

    case getType(pricerActions.setPricerType):
      return {
        ...state,
        transaction: {
          ...state.transaction,
          pricerType: action.payload.pricerType,
        },
      };

    case getType(pricerActions.getTransactionById.request):
      return {
        ...state,
        getTransactionFailed: false,
        transactionLoading: true,
      };
    case getType(pricerActions.getTransactionById.success): {
      const legs = [...action.payload.data.legs];
      const legsState = Object.values(legs).reduce(
        (
          acc: LegsState,
          obj: {
            leg_terms: LegTerms;
            leg_flows: FlowsState;
          },
          index,
        ) => {
          acc[index] = {
            ...obj,
            leg_valuation: [],
            leg_metrics: [],
          };
          return acc;
        },
        {},
      );

      return {
        ...state,
        transactionLoading: false,
        getTransactionFailed: false,
        hedgingInstrument: action.payload.data.trade_summary.instrument_type_id,
        transaction: {
          ...state.transaction,
          trade_summary: action.payload.data.trade_summary,
          pricerType: action.payload.data.pricerType,
          valuationDates: ValuationInitialState,
          legs: legsState,
          approval_request: action.payload.data.approval_request,
          option: isDef(action.payload.data.option)
            ? action.payload.data.option
            : state.transaction.option,
          fx_averaging: isDef(action.payload.data.fx_averaging)
            ? action.payload.data.fx_averaging
            : state.transaction.fx_averaging,
          execution_criteria: isDef(action.payload.data.execution_criteria)
            ? action.payload.data.execution_criteria
            : state.transaction.execution_criteria,
        },
      };
    }
    case getType(pricerActions.getTransactionById.failure):
      return {
        ...state,
        transactionLoading: false,
        getTransactionFailed: true,
      };

    case getType(pricerActions.buildSchedule.request):
      return {
        ...state,
        buildScheduleLoading: true,
      };
    case getType(pricerActions.buildSchedule.success):
      return {
        ...state,
        buildScheduleLoading: false,
        transaction: {
          ...state.transaction,
          legs: legsReducer(state.transaction.legs, action),
        },
      };
    case getType(pricerActions.buildSchedule.failure):
      return {
        ...state,
        buildScheduleLoading: false,
      };

    case getType(pricerActions.saveTrade.request):
      return {
        ...state,
        saveTradeLoading: true,
      };
    //TODO: change update after Andrey's update dor saving - spektor
    case getType(pricerActions.saveTrade.success):
      return {
        ...state,
        saveTradeLoading: false,
        transaction: {
          ...state.transaction,
          transaction_id: action.payload.transaction_id,
          // trade_summary: action.payload.trade_summary,
        },
      };
    case getType(pricerActions.saveTrade.failure):
      return {
        ...state,
        saveTradeLoading: false,
      };

    case getType(pricerActions.saveCopiedTrade.request):
      return {
        ...state,
        saveTradeLoading: true,
      };
    case getType(pricerActions.saveCopiedTrade.success):
      return {
        ...state,
        saveTradeLoading: false,
      };
    case getType(pricerActions.saveCopiedTrade.failure):
      return {
        ...state,
        saveTradeLoading: false,
      };

    case getType(pricerActions.updateTypeTrade.request):
      return {
        ...state,
        saveTradeLoading: true,
      };
    case getType(pricerActions.updateTypeTrade.success):
      return {
        ...state,
        saveTradeLoading: false,
        transaction: {
          ...state.transaction,
          trade_summary: action.payload.trade_summary,
          approval_request: action.payload.approval_request,
        },
      };
    case getType(pricerActions.updateTypeTrade.failure):
      return {
        ...state,
        saveTradeLoading: false,
      };

    case getType(pricerActions.deleteTrade.request):
      return {
        ...state,
        saveTradeLoading: true,
      };
    case getType(pricerActions.deleteTrade.success):
      return {
        ...state,
        saveTradeLoading: false,
        transaction: {
          ...state.transaction,
          trade_summary: action.payload.trade_summary,
        },
      };
    case getType(pricerActions.deleteTrade.failure):
      return {
        ...state,
        saveTradeLoading: false,
      };

    case getType(pricerActions.valueTrade.request):
      return {
        ...state,
        valueTradeLoading: true,
      };
    case getType(pricerActions.valueTrade.success):
      return {
        ...state,
        valueTradeLoading: false,
        transaction: {
          ...state.transaction,
          transaction_valuation: action.payload.data.valuation,
          transaction_metrics: action.payload.data.metrics,
          legs: legsReducer(state.transaction.legs, action),
        },
      };
    case getType(pricerActions.valueTrade.failure):
      return {
        ...state,
        valueTradeLoading: false,
      };

    case getType(pricerActions.deleteLeg): {
      const updatedState = { ...state.transaction.legs };
      delete updatedState[action.payload.legNumber];

      const recalculatedState = Object.values(updatedState).reduce(
        (acc: LegsState, obj, index) => {
          acc[index] = obj;
          return acc;
        },
        {},
      );
      return {
        ...state,
        transaction: {
          ...state.transaction,
          legs: { ...recalculatedState },
        },
      };
    }

    case getType(pricerActions.getFilesListDownload.request):
      return {
        ...state,
        filesForDownloadLoading: true,
      };
    case getType(pricerActions.getFilesListDownload.success):
      return {
        ...state,
        filesForDownloadLoading: false,
        filesForDownload: action.payload.data,
      };
    case getType(pricerActions.getFilesListDownload.failure):
      return {
        ...state,
        filesForDownloadLoading: false,
      };

    case getType(pricerActions.reviewTrade.request):
      return {
        ...state,
        reviewTradeLoading: true,
      };
    case getType(pricerActions.reviewTrade.success):
      return {
        ...state,
        reviewTradeLoading: false,
        transaction: {
          ...state.transaction,
          trade_summary: {
            ...state.transaction.trade_summary,
            ...action.payload.transactions[0],
          },
        },
      };
    case getType(pricerActions.reviewTrade.failure):
      return {
        ...state,
        reviewTradeLoading: false,
      };

    case getType(pricerActions.getFxSpotRate.request):
      return {
        ...state,
        options: {
          ...state.options,
          spot: {
            ...state.options.spot,
            loading: true,
          },
        },
      };
    case getType(pricerActions.getFxSpotRate.success):
      return {
        ...state,
        options: {
          ...state.options,
          spot: {
            data: action.payload.data,
            loading: false,
          },
        },
      };
    case getType(pricerActions.getFxSpotRate.failure):
      return {
        ...state,
        options: {
          ...state.options,
          spot: {
            data: null,
            loading: false,
          },
        },
      };

    case getType(pricerActions.getFxForwardRate.request):
      return {
        ...state,
        options: {
          ...state.options,
          forward: {
            ...state.options.forward,
            loading: true,
          },
        },
      };
    case getType(pricerActions.getFxForwardRate.success):
      return {
        ...state,
        options: {
          ...state.options,
          forward: {
            data: action.payload.data,
            loading: false,
          },
        },
      };
    case getType(pricerActions.getFxForwardRate.failure):
      return {
        ...state,
        options: {
          ...state.options,
          forward: {
            data: null,
            loading: false,
          },
        },
      };

    case getType(pricerActions.changeFxLocker):
      return {
        ...state,
        options: {
          ...state.options,
          fxRate: {
            ...state.options.fxRate,
            lockerValue: action.payload.lockerValue,
          },
        },
      };

    case getType(pricerActions.updateFxRateLegNotional):
      return {
        ...state,
        options: {
          ...state.options,
          fxRate: {
            ...state.options.fxRate,
            data: action.payload.fxRate,
          },
        },
        transaction: {
          ...state.transaction,
          legs: legsReducer(state.transaction.legs, action),
        },
      };

    case getType(pricerActions.setFxOption):
      return {
        ...state,
        transaction: {
          ...state.transaction,
          option: {
            ...state.transaction.option,
            ...action.payload,
          },
        },
      };

    case getType(pricerActions.setFxAveragingOption):
      return {
        ...state,
        transaction: {
          ...state.transaction,
          fx_averaging: {
            ...state.transaction.fx_averaging,
            ...action.payload,
          },
        },
      };

    case getType(pricerActions.setExecutionCriteria):
      return {
        ...state,
        transaction: {
          ...state.transaction,
          execution_criteria: {
            ...state.transaction.execution_criteria,
            ...action.payload,
          },
        },
      };

    case getType(pricerActions.resetAndChangeInstrumentType):
      return {
        ...state,
        transaction: {
          ...state.transaction,
          legs: {},
        },
      };

    case getType(pricerActions.changePricerTypeReset): {
      const oldPricerType = state.transaction.pricerType;
      return {
        ...{
          ...initialState,
          transaction: {
            ...initialState.transaction,
            pricerType: oldPricerType,
          },
        },
      };
    }

    case getType(pricerActions.changePercentage): {
      return {
        ...state,
        transaction: {
          ...state.transaction,
          percentage: action.payload.percentage,
        },
      };
    }

    case getType(pricerActions.reorderLegs.request):
      return {
        ...state,
        saveTradeLoading: true,
      };
    case getType(pricerActions.reorderLegs.success):
    case getType(pricerActions.reorderLegs.failure):
      return {
        ...state,
        saveTradeLoading: false,
      };

    case getType(pricerActions.reset):
      return {
        ...initialState,
      };

    default:
      return {
        ...state,
        transaction: {
          ...state.transaction,
          trade_summary: tradeSummaryReducer(
            state.transaction.trade_summary,
            action,
          ),
          legs: legsReducer(state.transaction.legs, action),
          master_leg: masterLegReducer(state.transaction.master_leg, action),
          valuationDates: valuationReducer(
            state.transaction.valuationDates,
            action,
          ),
        },
      };
  }
};

export default pricerReducer;
